/* screens/Clark/answer/answer.css — the answer rendering contract, in CSS.

   🔴 BUNDLE E · RULE 5 — SERIF IS THE VOICE OF THE LAW, AND IT NOW HAS THREE HOMES:
   the answer prose (.cc-ans-prose), case names (.cc-ans-cite, .cc-ans-card-cite) and quoted law
   (.cc-ans-authority-quote). The old header said serif appears EXACTLY ONCE and meant quoted law
   only; case names were MONOSPACE, which reads as code rather than as law and breaks Indigo R2.1
   (case names italicized). That is a citation-format defect, not a preference. Serif anywhere
   else — headings, titles, emphasis — is still wrong.

   🔴 AND A FINDING THIS FILE IS THE SCENE OF. Seven tokens it consumed are defined in NO
   stylesheet in this repo: --cc-ok, --cc-danger, --cc-warn, --cc-serif, --cc-mono, --cc-parchment,
   --cc-parchment-edge (plus --font-body / --font-ui). Written as `var(--cc-ok, #1d7a4c)`, an
   undefined token means the FALLBACK IS THE VALUE — always, on all 19 themes. So the treatment
   pill's good-law colour was a hardcoded green that could not flip to gold on the green-accent
   themes (the Damages collision rule, invariants.mjs GOODLAW_GOLD) and could not lift to the
   dark-card green on En Banc. The theme system computed the right answer and shipped it as
   --cc-success-* / --cc-warning-* / --cc-error-*; this file never asked for it.

   The rawcolor gate cannot see this class of bug by design — it strips `var(--x, #fallback)`
   before scanning, on the reasoning that a fallback is a safety net behind a real token. That
   reasoning holds only while the token exists. Every one is now a real contract role. */

.cc-sr-only {
  position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
  overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0;
}

/* ── Question echo — RULES DELETED WITH THE COMPONENT ──
   `QuestionEcho` rendered a "YOU ASKED" card restating the question that sits in the user's own
   bubble directly above it. Deleted in parts.jsx (BUNDLE E · LANE 2); its CSS goes with it rather
   than lingering as rules for an element nothing renders. */

/* 🔴 RUN GE — --cc-page IS NOT A SURFACE YOU CAN PUT CARD INK ON, AND FOUR PANELS IN THIS FILE DID.
   `page` is the theme's identity colour and it is NOT in the card family: on Gavel it is #3D2411, a
   dark brown, under a cream #FBF6EC card. Every one of these blocks then writes --cc-ink (#2A1D10)
   or --cc-ink-muted on it, so on Gavel they measured 1.14:1 and 2.15:1 — near-black on dark brown.
   On Precedent and En Banc the page happens to sit close to the card, which is why this survived
   four themes of eye checks and a gate that had never rendered the state.
   THE OVER-BLOCK IS A REAL COMPONENT DEFECT, NOT A GALLERY ARTEFACT — it will read the same inside
   Clark; the parts gallery is simply the only screen in the list that renders a failed verification.
   The other three had not been caught yet and are the same fault. --cc-card-alt is the contract's
   "surface between card and page (subtle panels ON cards)" and --cc-card-hover is its hover twin;
   ink, inkSecondary, inkMuted and accentText are all verified against both, in all 19 themes. */
/* ── Verification trace ── */
.cc-ans-trace {
  margin-top: 10px;
  padding: 10px 12px;
  border: 1px solid var(--cc-hairline);
  border-radius: var(--cc-radius-sm);
  background: var(--cc-card-alt);
}
.cc-ans-trace-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 3px; }

.cc-ans-trace-row {
  display: flex;
  align-items: baseline;
  gap: 7px;
  /* Mono: these are machine checks, and they should look like machine output. */
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.6875rem;
  line-height: 1.6;
  color: var(--cc-ink-muted);
  animation: cc-ans-trace-in 0.22s ease both;
}
@keyframes cc-ans-trace-in { from { opacity: 0; transform: translateY(-2px); } to { opacity: 1; transform: none; } }

.cc-ans-trace-glyph { flex: 0 0 auto; width: 9px; text-align: center; }
.cc-ans-trace-row[data-status="pass"] .cc-ans-trace-glyph { color: var(--cc-success-text); }
.cc-ans-trace-row[data-status="fail"] .cc-ans-trace-glyph { color: var(--cc-error-text); }
.cc-ans-trace-row[data-status="fail"] .cc-ans-trace-label { color: var(--cc-error-text); }
.cc-ans-trace-label { flex: 0 0 auto; }
.cc-ans-trace-detail { flex: 1 1 auto; min-width: 0; opacity: 0.72; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

.cc-ans-trace-collapse,
.cc-ans-trace-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 8px;
  padding: 4px 10px;
  border: 1px solid var(--cc-hairline);
  border-radius: var(--cc-radius-sm);
  background: var(--cc-card);
  color: var(--cc-ink-muted);
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.6875rem;
  cursor: pointer;
}
.cc-ans-trace-pill[data-failed="0"] { color: var(--cc-success-text); border-color: var(--cc-success-text); }
.cc-ans-trace-pill[data-failed="1"] { color: var(--cc-error-text); border-color: var(--cc-error-text); }

/* ── Prose cites: LINK-STYLE TEXT, never a box ── */
.cc-ans-cite {
  display: inline;
  padding: 0;
  border: 0;                       /* the point: no box in prose */
  background: none;
  /* 🔴 RULE 5 / INDIGO R2.1 — a case name is ITALICIZED, and it is not code. Serif italic at 1em
     (not the 0.875em the mono needed to stop it shouting) so the cite reads as part of the
     sentence carrying it, which is what a citation in prose is.
     --cc-accent-TEXT, not --cc-accent-FILL: the contract verifies accentText >=4.5:1 on card and
     accentFill only >=3:1, because one is for reading and the other is for filling. Using the fill
     colour as prose ink measured 3.89:1 on En Banc. */
  font-family: var(--cc-font-display);
  font-style: italic;
  font-size: 1em;
  color: var(--cc-accent-text);
  text-decoration: underline dotted;
  text-underline-offset: 2px;
  cursor: pointer;
}
.cc-ans-cite:hover { text-decoration-style: solid; }
.cc-ans-cite-marker { font-size: 0.72em; padding-left: 1px; }
.cc-ans-cite-copied {
  margin-left: 5px;
  font-size: 0.72em;
  color: var(--cc-success-text);
}
/* An unresolved cite is visibly unresolved. It never renders as plain prose. */
.cc-ans-cite[data-status="unverified"] {
  color: var(--cc-error-text);
  text-decoration-style: wavy;
}

/* ── Authority block — THE ONLY SERIF ── */
.cc-ans-authority {
  margin: 12px 0;
  padding: 12px 15px;
  /* Parchment tint, warm against the page well. */
  background: var(--cc-card-raised);
  border-left: 2px solid var(--cc-edge);
  border-radius: 0 var(--cc-radius-sm) var(--cc-radius-sm) 0;
}
.cc-ans-authority-quote {
  margin: 0;
  font-family: var(--cc-font-display);   /* ← the one serif */
  font-style: italic;
  font-size: 0.9375rem;
  line-height: 1.55;
  color: var(--cc-ink);
}
.cc-ans-authority-prov {
  margin-top: 7px;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.6875rem;
  color: var(--cc-ink-muted);
}

/* ── Treatment pill — verdict only, never colour alone ── */
.cc-tx-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 8px;
  border: 1px solid currentColor;
  border-radius: var(--cc-radius-sm);
  font-family: var(--cc-font-body);
  font-size: 0.6875rem;
  line-height: 1.5;
}
.cc-tx-pill[data-tone="clear"]    { color: var(--cc-success-text); }
.cc-tx-pill[data-tone="negative"] { color: var(--cc-error-text); }
.cc-tx-pill[data-tone="caution"]  { color: var(--cc-warning-text); }
.cc-tx-pill[data-tone="pending"]  { color: var(--cc-ink-muted); }
.cc-tx-pill-glyph { font-weight: 700; }

/* ── Authorities panel ── */
.cc-ans-panel {
  flex: 0 0 308px;
  width: 308px;
  min-width: 0;
  padding: 14px;
  border-left: 1px solid var(--cc-hairline);
  overflow-y: auto;
}
.cc-ans-panel-head {
  margin: 0 0 10px;
  font-family: var(--cc-font-body);
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--cc-ink-muted);
}
.cc-ans-panel-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 9px; }

/* ── 🔴 STREAM C — THE `.cc-ans-card*` FAMILY IS DELETED, EXCEPT THE ONE PART STILL RENDERED ──
   Clark's authority card is @citeclerk/ui's `.cc-auth` now, and `.cc-ans-card` survives only as a
   `legacyClass` on it — a SELECTOR the snapshot and authority-parity suites find rows by, carrying
   no styling of its own. So five rules here described elements that no JSX emits any more:

     .cc-ans-card          → the card's own box. Worse than dead: its 6px radius was measured
                             WINNING over the shared card's 10px (same specificity, later file),
                             so the mockup's `--r-md` could not land while it stood.
     .cc-ans-card-type     → replaced by .cc-auth__type
     .cc-ans-card-role     → replaced by .cc-auth__why (and unclamped from one line to three:
                             a verified sentence does not fit on one)
     .cc-ans-card-link     → the "Open in Research →" line. The whole card is the link.
     .cc-ans-card-hit      → the anchor. Now .cc-auth__hit.
     .cc-ans-card[data-variant="column"] .cc-ans-card-cite → the rail's un-boxed variant.

   Writing `:not(.cc-auth)` on the first of them was my first cut and it is the wrong answer HERE
   (it is the right one in Research's sheet, where `.wk-row` still styles the loading skeleton).
   Nothing renders `.cc-ans-card` without `.cc-auth`, so the guarded rule matched nothing at all —
   a rule that cannot fire is not a safety net, it is a rule the next reader has to disprove.

   `.cc-ans-card-cite` STAYS: TemplatedAnswer's `case-rows` tier and its wide table still render it
   (three call sites), and those are layouts, not cards. */

/* A BOX CHIP — legitimate in case rows and in the wide table, never in prose.
   🔴 RULE 5: the NAME inside it is serif italic wherever it appears. */
.cc-ans-card-cite {
  padding: 2px 7px;
  border: 1px solid var(--cc-hairline);
  border-radius: var(--cc-radius-sm);
  background: var(--cc-card-alt);      /* RUN GE — see the over-block note above */
  font-family: var(--cc-font-display);
  font-style: italic;
  font-size: 0.8125rem;
  color: var(--cc-ink);
}

/* ── Answer actions ── */
/* 🔴 STREAM C — THE ANSWER'S ACTION ROW, 1:1 WITH THE MOCKUP'S `.acts` / `.primary` / `.secondary`.
   Measured live against it: gap 7 vs 8, margin-top 22 vs 20, padding 6px 11px vs 8px 16px (the
   secondary's 8px 14px), font-size 12px vs 13px — so the buttons rendered 30px tall where the
   mockup's are 34px. Every one of those is small on its own and together they are most of why the
   answer's footer read cramped: a 30px control under 19px serif prose is under-scaled for it.
   `font-weight: 600` on the primary is the mockup's `font:600 13px` shorthand. */
.cc-ans-actions { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 20px; }
/* RUN GE - the answer panel's action buttons carried `hairline`, the SEPARATOR role, as their only
   edge: 1.47:1 on Precedent, 1.70:1 on En Banc and System. `edge` is the control-boundary role and
   generate.mjs derives it to >=3:1 per theme. The hover state already goes to --cc-accent-fill, so
   the resting state was the quietest the control ever got. */
.cc-ans-action {
  padding: 8px 14px;
  border: 1px solid var(--cc-edge);
  border-radius: var(--cc-radius-sm);
  background: var(--cc-card);
  color: var(--cc-ink);
  font-family: var(--cc-font-body);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
}
.cc-ans-action--primary { padding: 8px 16px; font-weight: 600; }
.cc-ans-action:hover { border-color: var(--cc-accent-fill, #002368); background: var(--cc-card-hover); }

/* ── Over-block: a designed moment, not an error box ── */
.cc-ans-overblock {
  margin: 12px 0;
  padding: 13px 15px;
  border: 1px solid var(--cc-warning-text);
  border-radius: var(--cc-radius-sm);
  background: var(--cc-card-alt);
}
.cc-ans-overblock-head {
  display: flex;
  align-items: baseline;
  gap: 7px;
  margin: 0;
  font-family: var(--cc-font-body);
  font-size: 0.875rem;
  color: var(--cc-ink);
}
.cc-ans-overblock-glyph {
  flex: 0 0 auto;
  display: inline-flex; align-items: center; justify-content: center;
  width: 15px; height: 15px;
  border-radius: 50%;
  background: var(--cc-warning-text);
  color: var(--cc-on-status);
  font-size: 0.625rem; font-weight: 700;
}
.cc-ans-overblock-q { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.9em; }
.cc-ans-overblock-sub {
  margin: 7px 0 0;
  font-family: var(--cc-font-body);
  font-size: 0.8125rem;
  color: var(--cc-ink-muted);
}
.cc-ans-overblock-list { list-style: none; margin: 8px 0 0; padding: 0; display: flex; flex-direction: column; gap: 5px; }
.cc-ans-overblock-pick {
  display: flex; flex-direction: column; gap: 2px;
  width: 100%; padding: 8px 10px; text-align: left;
  border: 1px solid var(--cc-edge);   /* RUN GE - see .cc-ans-action above */
  border-radius: var(--cc-radius-sm);
  background: var(--cc-card);
  cursor: pointer;
}
.cc-ans-overblock-pick:hover { border-color: var(--cc-accent-fill, #002368); }
.cc-ans-overblock-cite { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: 0.75rem; color: var(--cc-ink); }
.cc-ans-overblock-detail { font-family: var(--cc-font-body); font-size: 0.6875rem; color: var(--cc-ink-muted); }
.cc-ans-overblock-dismiss {
  margin-top: 9px; padding: 0; border: 0; background: none;
  font-family: var(--cc-font-body); font-size: 0.6875rem;
  color: var(--cc-ink-muted); cursor: pointer; text-decoration: underline;
}

/* ── Focus: visible everywhere, on every interactive part ── */
.cc-ans-cite:focus-visible,
.cc-ans-action:focus-visible,
.cc-ans-trace-pill:focus-visible,
.cc-ans-trace-collapse:focus-visible,
.cc-ans-overblock-pick:focus-visible,
.cc-ans-overblock-dismiss:focus-visible {
  outline: 2px solid var(--cc-accent-fill, #002368);
  outline-offset: 2px;
  border-radius: 3px;
}

/* The panel drops below the answer under 1220px rather than squeezing both. */
@media (max-width: 1219.98px) {
  .cc-ans-panel {
    flex-basis: auto;
    width: 100%;
    border-left: 0;
    border-top: 1px solid var(--cc-hairline);
  }
  .cc-ans-panel-list { flex-direction: row; flex-wrap: wrap; }
  .cc-ans-card { flex: 1 1 240px; }
}

@media (prefers-reduced-motion: reduce) {
  /* ⚠️ THIS BLOCK KEYS ON SELECTORS, NOT ON THE ANIMATION NAME. Anything that reuses
     `cc-ans-trace-in` must be listed here BY NAME or it keeps animating for a user who asked it
     not to — an opt-out that silently covers only the rules someone remembered is worse than none.
     `.cc-ans-stage` (RUN AP) is the second user of that keyframe and is listed for that reason. */
  .cc-ans-trace-row, .cc-ans-stage { animation: none; }
  .cc-ans-action, .cc-ans-cite { transition: none; }
}

/* ── 🔴 CLARK B6 — THE UNSCOPED DISCLOSURE ─────────────────────────────────────────────────────
   B6 converted `not_binding` from a REJECTION into a LABEL when the user has selected no
   jurisdiction. This is the label. It is the half of the trade that keeps the other half honest,
   so it is styled to be READ, not to be dismissed: a left rule and the warn hue, at body size
   rather than the 0.75rem the footnotes use. It sits ABOVE the prose it qualifies.

   It deliberately does NOT reuse `.cc-tpl-note` (0.75rem, muted): a variant note is Clark talking
   about its own process, and this is Clark limiting the weight of the law it just stated. */
.cc-ans-persuasive-notice {
  margin: 0 0 12px;
  padding: 8px 0 8px 12px;
  border-left: 2px solid var(--cc-warning-text);
  font-family: var(--cc-font-body);
  font-size: 0.875rem;
  line-height: 1.5;
  color: var(--cc-ink-secondary);
  max-width: var(--cc-clark-prose, 720px);
}

/* ── BUNDLE 2 · AMENDMENT A3 — the advice notice ──────────────────────────────────────────
   🔴 IT IS DELIBERATELY NOT STYLED AS A WARNING. The persuasive notice above it uses a warn
   border because it qualifies the STRENGTH of the authority — something the reader must weigh.
   This one qualifies nothing about the answer: the answer is as good as any other. It states a
   boundary about what Clark will do with it. Painting that amber would make an accurate answer
   look suspect, which is the visual form of the hedging STEP 2 deleted. So: the same quiet
   left-rule, in the hairline colour, at body weight. Present, not alarming. */
.cc-ans-advice-notice {
  margin: 0 0 12px;
  padding: 8px 0 8px 12px;
  border-left: 2px solid var(--cc-hairline);
  font-family: var(--cc-font-body);
  font-size: 0.875rem;
  line-height: 1.5;
  color: var(--cc-ink-muted);
  max-width: var(--cc-clark-prose, 720px);
}

/* An authority with no cluster id gets words, not a dead link. That copy is now rendered by the
   shared card's `unlinkedNote` slot and styled by `.cc-auth__unlinked` — same words, same italic,
   same reason. */

/* ── BUNDLE 2 · STEP 4 — the Sources column ───────────────────────────────────────────────
   It shares .cc-ans-card with the inline panel by design: the card is the shared component, so
   its styling is inherited rather than restated. Only the CONTAINER is new. */
/* 🔴 BUNDLE E — --cc-card, NOT --cc-page, AND THE THEME CHECK IS WHY.
   `--cc-page` is THE COLOURED PAGE — the theme's identity colour, saturated green on Damages and
   near-cyan on the Misdemeanor line. Every text token in this rail is from the --cc-ink family,
   which the contract contrast-verifies against CARD and says nothing about against page. Measured:
   the Sources heading and the as-of line came out at 1.34:1 on Damages and 2.34:1 on m5-turquoise.
   Unreadable, on two of the four themes checked, and invisible to every gate in the repo.
   This is the exact mistake the rail comment in ClarkShell.css warns about, made two files later.
   Card ground + ink family is the pairing the contract actually guarantees.

   🔴 BUNDLE 1to1 · LANE 3 — 280px AND `--cc-card`, WHICH IS THE MOCKUP'S `.rail` EXACTLY.
   Width was 300 against the mockup's `--rail: 280px` — the SAME variable it spends on the history
   rail, so the two rails were 264 and 300 where the spec has one number for both. Ground moves
   card-alt → card for the same reason: the mockup's `.rail` is `--card`, the history rail already
   is, and with the stage now on `--cc-stage` a card rail is what makes both edges of the
   conversation read as panels ON a ground rather than as three near-identical near-whites. The
   contrast argument above is strengthened, not weakened — ink is verified against CARD. */
.cc-clark-sources {
  flex: 0 0 280px;
  min-width: 0;
  overflow-y: auto;
  /* STREAM C: the mockup's `.rail` padding is `20px 16px` flat. The extra 32px of bottom padding
     was scroll-clearance for a rail that ends at the viewport; the rail scrolls itself. */
  padding: 20px 16px;
  border-left: 1px solid var(--cc-hairline);
  /* ══ 🔴 RUN VC · CHANGE 3 — THE GROUND MOVES, BECAUSE THE GROUND IS THE DEFECT. ══════════════
     Adam, 2026-08-25: *"cards white and substantial."* ⭐ RUN PC's finding is the mechanism — **on
     a coloured ground white cards have depth; on white they vanish** — and the card already HAS
     everything Adam asked for. `packages/ui/react/AuthorityCard.css:56-59` gives it
     `background: var(--cc-card)`, `border: 1px solid var(--cc-edge)`, `border-radius: 10px` and
     `box-shadow: var(--cc-shadow-card)`. MEASURED before this change (ops/run-vc/out/probe-before
     .json): the card's computed fill is `rgb(255,255,255)` and so is the rail's. The card is
     invisible not because it lacks a fill but because it has the SAME fill as the thing behind it —
     card-against-ground was **1.00:1**, an identity, in every one of the nineteen themes.

     So the rail gets a ground of its own and the `--cc-card` cards sit ON it.

     🔴🔴 AND THE OBVIOUS GROUND — `--cc-stage`, the one the conversation already stands on — WAS
     MEASURED AND REFUSED. It gives the best depth on offer (card-against-ground **1.04:1 to
     1.19:1**, and in `dark` the card is LIGHTER than the stage, so it reads in both directions).
     It also takes the card's OUTER edge down with it, because that edge now separates the card
     from the ground instead of from an identical fill:

         edge against ground        token math   RENDERED PIXELS
         --cc-card   (before)          3.38          3.32
         --cc-stage  (refused)         3.03          **2.97**   ← m2-green, m3-yellow. Bar is 3.
         --cc-card-alt (shipped)       3.31          3.25

     ⭐ THE TOKEN MATH SAID `--cc-stage` CLEARED. THE MONITOR SAID IT DID NOT. A 1px border at
     deviceScaleFactor 2 with a 10px radius is ANTIALIASED — no pixel on it is ever the pure token
     value — and the loss is a consistent ~0.06. That is the entire reason this run measures every
     pair twice with two readers that share no probe point (ops/run-vc/contrast.mjs): the analytic
     reader is wrong in the FLATTERING direction here, and a run that trusted it would have shipped
     three themes under the floor on the strength of a green check. The brief's instruction was *do
     not spend that 0.03* and `--cc-stage` spends 0.06 more than exists.

     ⚠️ WHAT `--cc-card-alt` COSTS INSTEAD, STATED RATHER THAN LEFT TO BE FOUND. Its depth is
     thinner: **1.007:1 (m3-yellow) to 1.10:1**. On m3-yellow specifically the fill difference is
     effectively nil and the card reads by its EDGE and its SHADOW alone — which is why the shadow
     below is not decoration. Every other pair on the rail improves against this ground rather than
     degrading (muted ink 4.61 → 5.12, accent text 4.58 → 4.93). All nineteen themes, both methods,
     in ops/run-vc/out/contrast.json.

     🔴 `--cc-edge` IS NOT TOUCHED and no new token is introduced. RUN RC's m3-yellow card-edge
     number is not spent — it is improved on, from 3.38 to 3.31 against a moved ground, and the
     3:1 floor is cleared by 0.25 rendered rather than by 0.03 on paper. */
  background: var(--cc-card-alt);
}

/* 🔴 BUNDLE V7 · LANE 7 — THE RAIL HAD NO BREAKPOINT AT ALL, AND IT NEVER COLLAPSED.
   Measured with an answer on screen (probe/rail-widths.cjs), the rail stayed a right-hand column
   at every width: 300px at 1440, 258 at 1100, 254 at 820, and 132px at 380 — where it squeezed
   the thread to 249px and left a case citation about eighteen characters of room. Nothing
   overflowed the viewport, which is exactly why this survived: a layout that does not overflow is
   not the same as one that works, and only the second is worth shipping to someone on a phone.

   ⚠️ DEPARTURE FROM THE MOCKUP, STATED. The mockup collapses at 1100
   (`@media(max-width:1100px){.results{grid-template-columns:1fr}}`). It is done at 900 here
   because 1100 was MEASURED as still working — rail 258px beside an 842px thread, both usable —
   and stacking a working two-column layout would be a regression dressed as conformance. 900 is
   the width below which the rail stops being a column and starts being a gutter.

   `max-height` rather than a free height: stacked, the rail must not push the thread off the top
   of a phone screen; it becomes a bounded, scrollable block under the conversation. */
/* 🔴 RUN VJ — THE RAIL DECLARATIONS THAT USED TO SIT HERE WERE DEAD, AND ARE DELETED.
   This block carried a `.cc-clark-sources` rule of its own: `flex: 0 0 auto`, a `max-height` of
   42vh, `border-left: 0` and a 1px `border-top`. NOT ONE OF THOSE FOUR HAS EVER APPLIED. The rail
   block 300 lines below matched wherever this one did — it said 1199 where this says 900 — and
   being later it won every cascade, every time, on all four properties. MEASURED before deleting,
   not argued: at 400px and 880px, the two widths this block owns, `max-height` computes to
   **280px — 40vh of a 700px viewport, the LOWER block's number** — and never to the 294px this
   block asked for, and `flex` computes to `1 1 auto` and never to this block's `0 0 auto`
   (`ops/run-vj/out/computed-before.json`). ⭐ A DECLARATION A READER WOULD THINK IS LIVE IS WORSE
   THAN NO DECLARATION: 42vh was the number a reader would reach for, and moving it would have
   changed nothing and cost an afternoon proving that.
   ⚠️ AND NOTE WHY IT COULD NOT SIMPLY BE LEFT. Now that RUN VJ has moved the block below to 900,
   the two conditions are IDENTICAL and SOURCE ORDER IS THE ONLY THING DECIDING between two rules
   that disagree. That is exactly readable enough to be correct and not readable enough to be safe.
   One block, one condition, one set of rail declarations — the one that has always won.
   What remains here is the single declaration in this block that is live and unique: main becomes
   a column, which is what makes the rail stack at all. */
@media (max-width: 900px) {
  .cc-clark-main { flex-direction: column; }
}

/* 🔴 STREAM C — THE RAIL HEADING IS THE MOCKUP'S `.rail-h`, AND THE WORD IS "AUTHORITIES".
   Two changes from two different instructions, and they do not conflict:
     · the WORD comes from the brief — Clark said `SOURCES`, Research says `AUTHORITIES`, and a
       "source" is anything you read while an AUTHORITY is something you can cite. This rail lists
       the second.
     · the TREATMENT comes from the mockup — 10.5px/600/uppercase/+0.1em/ink-muted, margin
       `0 0 14px`. Measured live at 12px/+0.06em/12px, which is what made the rail's headings read
       heavier and looser than the design. Research's 16px sentence case is NOT adopted: it is
       Research's rail, and v7 specifies Clark's. */
.cc-clark-sources-head {
  margin: 0 0 14px;
  font-family: var(--cc-font-body);
  /* 1.5 is the mockup's inherited body line-height (15.75px at 10.5px); ours computed `normal`,
     which is ~1.2 for this face — a 3px difference in the heading's own box, and the reason the
     rail's top edge sat tighter than the design's. */
  line-height: 1.5;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--cc-ink-muted);
}

/* ── BUNDLE C · DEFECT 2 — the rail's turn header ──────────────────────────────────────────
   The rail is scoped to ONE answer and says which. Tokens only (no literals): --cc-brand-dark
   is defined NOWHERE in this repo (RAWCOLOR arc) and painting with it would render transparent,
   so every colour here is one the shell actually ships. */
/* 🔴 BUNDLE UI · LANE 2 — ONE BLOCK PER TURN, STACKED, NEWEST AT THE BOTTOM.
   Chronological, so the rail scrolls in sync with the thread and the question a block belongs to
   is always written directly above it. `:first-of-type` carries no rule above it, so the first
   block does not open with a stray divider under the "Sources" heading. */
.cc-clark-sources-turn {
  border-top: 1px solid var(--cc-hairline);
  padding-top: 14px;
  margin-top: 16px;
}
.cc-clark-sources-turn:first-of-type {
  border-top: 0;
  padding-top: 0;
  margin-top: 0;
}

/* 🔴 THE ← → PAGER IS DELETED, NOT LEFT BEHIND.
   `.cc-clark-sources-nav`, `.cc-clark-sources-navbtn` (+ :hover/:disabled) and
   `.cc-clark-sources-count` styled Bundle C's "Answer N of M" control. Nothing renders those
   class names any more (AuthorityCard.jsx), so keeping the rules would be dead CSS pretending to
   be live — the same thing this file's own "Hero masthead — DELETED" note is about. */

/* 🔴 BUNDLE UI · LANE 3 — the scope this turn was answered under, beside the question it answered.
   --cc-accent-TEXT (≥4.5:1 against card), never --cc-accent-fill. */
.cc-clark-sources-scope {
  margin: 5px 0 0;
  font-family: var(--cc-font-body);
  font-size: 0.6875rem;
  font-weight: 600;
  color: var(--cc-accent-text);
}

/* The question this rail belongs to. Clamped to two lines: it is a label, not the answer. */
/* The mockup's `.rail-q`: 12.5px, 600, line-height 1.35, 10px under. It is the question the block
   belongs to, so it is set as a label — weight rather than size. Measured 13px/400/1.45. */
.cc-clark-sources-q {
  margin: 0 0 10px;
  font-family: var(--cc-font-body);
  font-size: 12.5px;
  font-weight: 600;
  line-height: 1.35;
  color: var(--cc-ink);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* 🔴 THE AS-OF LINE. A treatment badge without a date is a claim about NOW; with one it is a
   claim about a moment, which is the only claim the citator can support. */
.cc-clark-sources-asof {
  margin: 6px 0 0;
  font-family: var(--cc-font-body);
  font-size: 0.6875rem;
  line-height: 1.4;
  color: var(--cc-ink-muted);
}

/* The mockup's `.group`: 10.5px/600/uppercase/+0.09em, margin `12px 0 7px`. Measured
   11px/+0.05em/`20px 0 10px` — the extra 8px above and 3px below is what spread the rail out. */
.cc-clark-sources-subhead {
  margin: 12px 0 7px;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.09em;
  text-transform: uppercase;
  color: var(--cc-ink-muted);
}

/* 🔴 THE EMPTY STATE IS TYPE, NOT A BLANK. A column that renders nothing is indistinguishable
   from a column that broke — the same no-blanks rule the answer states are built on. */
/* The mockup's `.status`: 12.5px, ink-muted, ITALIC, margin `10px 0 4px`. The italic is the
   design's own distinction between a status and a heading — the comment below already argues for
   it in words ("Quiet, sentence case, italic") and the rule was set roman. Measured 13px/normal. */
.cc-clark-sources-empty {
  margin: 10px 0 4px;
  font-family: var(--cc-font-body);
  font-size: 12.5px;
  font-style: italic;
  line-height: 1.5;
  color: var(--cc-ink-muted);
}
/* 🔴 A PENDING STATE IS A STATUS, NOT A GROUP. It was previously set in the same small-caps
   letterspaced treatment as "RELIED ON", so it read as a heading for a section that will never
   exist. Quiet, sentence case, italic — a thing that is happening, not a thing that is there. */
.cc-clark-sources-empty[data-pending="1"] { font-style: italic; }

/* ══ 🔴 RUN VC — THE RAIL ENTRY: AN EYEBROW AND A CARD, INSIDE ONE LIST ITEM. ══════════════════
   The card used to BE the `<li>`. Adam's change #4 puts a document-type label above it, so the
   entry is now the pair and the card is a `<div>` inside it (AuthorityCard.jsx passes
   `as={isColumn ? "div" : "li"}` — a prop the shared component already has, so no packages/ui edit
   and no change to Research). These three rules move the list bullet suppression and the 7px
   inter-card rhythm from the card onto the item, unchanged in value: `.rcard`'s
   `margin-bottom: 7px` is the mockup's and it is preserved exactly. ⭐ The card's WEIGHT changes
   in this run; the grid does not. */
.cc-clark-sources-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.cc-clark-sources-item {
  list-style: none;
  margin: 0 0 7px;
}
/* The card carried the rhythm when it was the item. It is not the item any more, and leaving the
   margin on it would double the gap. */
.cc-clark-sources .cc-auth {
  margin-bottom: 0;
}
/* 🔴 THE SECOND HALF OF "SUBSTANTIAL", AND IT COSTS NO CONTRAST. `--cc-shadow-card` is
   `0 1px 3px` at 4% — a border with a rumour under it. `--cc-shadow-md` is `0 2px 8px` at 10% and
   is what makes the card read as a sheet lying on the stage rather than an outline drawn on it.
   ⭐ A shadow is not measured by the contrast bar, so this buys depth without spending any of the
   3.03 the edge is now sitting on. Per-theme token (dark themes carry their own family), which is
   also what keeps it off ops/enforce/rawcolor-gate.mjs's ratchet.

   🔴 RUN CR · BLOCK 7 — TWO CORRECTIONS, ONE STALE AND ONE ADAM'S.
   ① THE FENCE SENTENCE IS NO LONGER TRUE AND IS REPLACED. It read *"FENCED TO THE RAIL: Research's
      cards keep `--cc-shadow-card`, because this is a statement about THIS ground."* RUN RD · R7
      later gave Research's rail rows this same `--cc-shadow-md` (workspace.css records the two
      values side by side), so both products' authority cards have carried the heavier shadow since.
   ② THE INLINE PANEL JOINS THEM. Adam: *"Needs to look 1:1 with Research."* MEASURED across all
      three surfaces (ops/run-cr/clark/out-panel.json + the Research probe):
          Research authority card   rgba(0,35,104,.10) 0 2px 8px   (--cc-shadow-md)
          Clark RAIL card           rgba(0,35,104,.10) 0 2px 8px   (--cc-shadow-md)
          Clark PANEL card          rgba(10,22,40,.04) 0 1px 3px   (--cc-shadow-card)  ← the odd one
      The panel was the only AUTHORITY surface still on the base shadow.
   ⚠️ AND THE SEE-ALSO CARDS ARE DELIBERATELY LEFT ON `--cc-shadow-card`, in BOTH apps. Research's
      `.wk-seealso__card` measures `0 1px 3px` at 4% today and keeps it: those rows are not
      authorities this answer rests on, and the lighter sheet is the one distinction between the
      two groups that survives Adam's removal of every sentence about them. */
.cc-clark-sources .cc-auth,
.cc-ans-panel-list .cc-auth {
  box-shadow: var(--cc-shadow-md);
}

/* 🔴 RUN VL — RUN VC's DOCUMENT-TYPE EYEBROW RULE IS DELETED, AND THE DELETION IS THE POINT.
   `.cc-clark-sources-eyebrow` was the bare small-caps `CASE` label above a See-also row. RUN VA
   moved the resolved groups' type INSIDE the card as a glassy chip and left this rule serving only
   the See-also branch; that branch now renders the same chip through
   `.cc-clark-sources-item .cc-auth__type` below, so nothing in the app produces this class any
   more — grepped across every jsx/js/mjs/html outside ops/, not assumed.
   ⭐ IT IS REMOVED RATHER THAN LEFT ORPHANED because an orphan that still LOOKS like the See-also
   eyebrow is precisely how a bare eyebrow gets re-added next to a card that already has a chip.
   ⚠️ Three RUN VC probes still SELECT it (ops/run-vc/{verify,probe,contrast}.mjs). They are that
   run's record of the surface as it stood and are not rewritten here; they will read null, which is
   the correct reading of an element that is gone. */

/* ══ 🔴🔴 RUN VA — THE TYPE IS A GLASSY CHIP, INSIDE THE CARD, AT THE TOP. ═══════════════════════
   Adam, 2026-08-25, two instructions in sequence: *"I want the authority type at the top INSIDE the
   card. Not above the card."* and *"maybe we should use the same authority chips (glassy looking,
   colored) that we use in Citations app in history — I think that would be better for
   consistency."*

   ⭐ IT IS THE CITATIONS CHIP, NOT A LOOKALIKE. Same three tokens per type, same low-alpha fill /
   saturated text / hairline border construction, same 4px radius, as `.cc-rail-chip` in
   chrome/RightPanel.css — which Recent History and the donut legend already share. Restating the
   VALUES here is how two chips drift apart, so only the geometry differs (see below) and the colour
   comes from the same `--cc-type-*` triple both surfaces read.

   ⚠️ NOT `@extend`-ed and not a shared class, deliberately: `.cc-rail-chip` is styled in
   `chrome/RightPanel.css`, which Clark does not load. Adding an import to pull in one rule would
   drag the whole right-panel stylesheet onto this surface. Two selectors, one token vocabulary.

   🔴 THE COLOUR KEYS OFF THE ITEM, NOT THE SPAN. `.cc-auth__type` is packages/ui's element and it
   carries no per-type class. Selecting on the rail item's `data-kind` keeps this a CLARK SKIN over
   a shared component: nothing is added to packages/ui, and Research and Library keep the quiet grey
   uppercase label they have today. `.cc-auth__type`'s base rule is untouched.

   ⭐ ONLY TWO TYPES CAN REACH THIS RAIL. `railPayload.js:145,199` folds `kind` to exactly `"Case"`
   or `"Statute"` and nothing else, so the seven-type vocabulary is not needed here — and, more to
   the point, `.cc-rail-chip--book` and `--journal` are the two members of that vocabulary carrying
   HARD-CODED rgba rather than tokens. They are unreachable from this surface, so none of that
   raw colour comes with the chip. All six tokens below are defined in 19 of 19 theme blocks in
   BOTH themes.css copies; checked, not assumed.

   ⚠️ AND THE RISK, STATED, BECAUSE IT IS THE ONE THING THIS CHANGE COULD GET WRONG.
   RUN VC put the label outside the card to avoid a coloured object on a row reading as a CITATOR
   VERDICT — good law / bad law — which Adam's treatment ruling keeps off this rail entirely. A
   red-tinted chip beside a case name is exactly the shape that misreading takes. What defuses it is
   that the hue is a function of the DOCUMENT TYPE and nothing else: every case is the same red on
   every row of every answer, whatever its treatment, so the colour cannot vary with a verdict and
   therefore cannot encode one. It is also the identical hue the user has already learned in
   Citations history, which is Adam's consistency argument doing real work. Reverting is deleting
   this block; the label stays inside the card, quiet and grey, with nothing else to change. */
/* 🔴 RUN CR · BLOCK 7 — `.cc-ans-card[data-kind]` JOINS THE SKIN, so Clark's INLINE panel draws
   the same chip as Clark's rail and as Research. The selector below was the rail item's `<li>`
   only; the panel's cards are not inside one, so they drew the shared component's grey uppercase
   label. Adam: *"need to have the source chips … 1:1 with Research."* Same tokens, same box, one
   more selector, and `packages/ui` still untouched. */
.cc-clark-sources-item .cc-auth__type,
.cc-ans-card[data-kind] .cc-auth__type {
  display: inline-flex;
  align-items: center;
  /* The Citations chip is 10px/0.02em at `padding: 1px 6px`; this one keeps that box and drops
     the uppercase transform with it — `.cc-rail-chip` is title-case ("Case", not "CASE") and the
     payload already sends the word title-cased. The deleted eyebrow was uppercase because it was a
     LABEL ON A LIST; a chip is an object on the card and reads as one word. */
  font-family: var(--cc-font-body);
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.02em;
  line-height: 1.5;
  text-transform: none;
  padding: 1px 6px;
  border-radius: 4px;
  white-space: nowrap;
  /* Default is Case, matching `.cc-rail-chip`'s own default, so a row whose kind never arrives
     draws a chip in the commonest type rather than an unstyled word. */
  background: var(--cc-type-case-bg);
  color: var(--cc-type-case);
  border: 0.5px solid var(--cc-type-case-border);
}
.cc-clark-sources-item[data-kind="statute"] .cc-auth__type,
.cc-ans-card[data-kind="statute"] .cc-auth__type {
  background: var(--cc-type-statute-bg);
  color: var(--cc-type-statute);
  border-color: var(--cc-type-statute-border);
}
/* 🔴 RUN RQ · §2 — THE REGULATION CHIP. `29 C.F.R. Part 541`, driven cold on 2026-09-01, came back
   stripped and wore the **Case** chip — not by falling through to a neutral default but by hitting
   the one four lines above, whose own comment says *"Default is Case … so a row whose kind never
   arrives draws a chip in the commonest type"*. That default is right for a MISSING kind and wrong
   for a kind we can read off the citation, which is what `seeAlsoKind.js` now does.
   ⭐ NO NEW HEX. `--cc-type-regulation*` is one of the seven type families `packages/ui/themes.css`
   has emitted for all 19 themes since the generator was written; this is the first rail consumer.
   `rawcolor-gate` is down-only and its floor cannot move for a var(). */
.cc-clark-sources-item[data-kind="regulation"] .cc-auth__type,
.cc-ans-card[data-kind="regulation"] .cc-auth__type {
  background: var(--cc-type-regulation-bg);
  color: var(--cc-type-regulation);
  border-color: var(--cc-type-regulation-border);
}

/* The chip is a box now, not a line of text, so it needs air under it that a 10px label did not.
   `.cc-auth__typeline` is packages/ui's flex row and its gap is horizontal; this is the vertical
   step down to the case name, set here rather than in the shared file because Research's card has
   no chip to make room for. */
.cc-clark-sources-item .cc-auth__typeline {
  margin-bottom: 4px;
}

/* 🔴 RUN VC · DEFECT ZERO — THE STATED ABSENCE. The question line is unconditional now; when the
   turn carries no question it says so in its own voice. Italic and muted so it cannot be mistaken
   for a question that WAS asked, and present so it cannot be mistaken for the question above it —
   which, when the line was `{q && …}`, is exactly what happened. */
.cc-clark-sources-q[data-q-absent="1"] {
  font-weight: 400;
  font-style: italic;
  color: var(--cc-ink-muted);
}

/* ══ 🔴🔴 RUN VL — `SEE ALSO` BECOMES CARDS. ADAM OVERRULES RUN VC, 2026-08-25. ═════════════════
   *"The cases and other authorities under See also should be in cards just like the Relied on
   cards. It looks like SHIT as is. I think they should be the same but the authority name should
   be black instead of blue."*

   VC's argument for rows is preserved here because it is the argument this rule now has to answer:
   *a card is the thing you click to read the authority, and there is no authority to read.* ⭐ THE
   HALF OF IT THAT WAS RIGHT IS KEPT AND IS NOW STRUCTURAL RATHER THAN VISUAL: the card is still
   not clickable, and not because it is styled to look inert — because no `href` and no `onSelect`
   reach the shared component, so its `is-unlinked` branch renders a plain `<div>`. There is no
   anchor to suppress. ZERO anchors and ZERO buttons, asserted on the rendered DOM by
   ops/run-vl/probe/rail.mjs, because "not clickable" is a claim about the DOM and a stylesheet
   cannot make it — VC's own sentence, and the reason its half survives its layout being reversed.

   🔴 THERE ARE ONLY FOUR RULES BELOW, AND THREE OF THEM ARE ABOUT WHAT IS *NOT* A CASE NAME.
   The card shell is not restated here AT ALL. The `<li>` carries `cc-clark-sources-item`, so fill,
   edge, radius, shadow, padding, the 7px rhythm and the glassy type chip all arrive through the
   rules the relied group already uses. ⭐ Two cards that share a selector cannot drift; two cards
   that share a NUMBER drift the first time one number is edited. */

/* 🔴🔴 THE ONE DIFFERENCE ADAM ASKED FOR, AND IT IS THE WHOLE OF IT.
   `.cc-auth__nm` is `--cc-accent-text` — the blue that means "this opens". RUN PJ already ruled on
   it and was overruled on the layout, not on this: *"a blue italic case name is an affordance a
   card with nothing to open cannot honour, and it was the same treatment worn by the six
   authorities we DID verify, three inches away."* Same serif, same italic, same 13px, same weight,
   same line-height. Colour is the only member of the set that differs. */
.cc-clark-sources-sa .cc-auth__nm {
  color: var(--cc-ink);
}

/* ══ 🔴 [RUN AB · BLOCK 5] THE GREY GROUND, THE SAME CHANGE RESEARCH TAKES IN THE SAME RUN. ══════
   Adam describes the group as *"black text on a grey ground rather than blue on white"*. Three of
   the four properties were already here — ink instead of the accent (above), the hover affordance
   removed (below), and no anchor or button on the card at all — and the ground was the relied
   cards' own white. A card that means "we do not hold this" sitting on the same ground as the six
   we DID verify, three inches away, is the weaker half of the statement this group exists to make.

   ⭐ THE TOKEN IS THE THEME'S CARD-ALT ROLE, not a hex: `rawcolor-gate` scans this file and the
   floor must not move (506 in-scope / 557 repo-wide). Research's twin rule is
   `.wk-seealso__card{background:var(--cc-bg-subtle)}` in workspace.css — two files because the two
   apps do not share a stylesheet, one ruling, landed together so they cannot drift apart. */
.cc-clark-sources-sa .cc-auth {
  background: var(--cc-bg-subtle);
}

/* 🔴 THE HOVER, WHICH IS THE ONE PLACE "THE SAME" CANNOT BE TAKEN LITERALLY — AND IT IS DECLARED
   AS SUCH RATHER THAN SLIPPED IN. `.cc-auth:hover` moves the border to `--cc-accent-text`: on a
   relied card that is a promise the click keeps, and on this card it is a promise that resolves to
   nothing. ⭐ Removing an affordance is not inventing a differentiator — it is the same ruling as
   the name colour, applied to the other property that says "clickable". Reverting is deleting
   these three lines; the cards then light up under the pointer and open nothing. */
.cc-clark-sources-sa .cc-auth:hover {
  border-color: var(--cc-edge);
}

/* 🔴 A SENTENCE MAY NOT WEAR A CASE NAME'S FACE. `seeAlsoLabel` has three shapes and only one of
   them is a NAME; the other two are sentences the product speaks in its own voice — *"The answer
   gave only a citation, …"* and *"An authority this answer named could not be identified."*
   parts.jsx's rule 2 sets case names in serif italic BECAUSE Indigo R2.1 requires case names
   italicised, so a sentence in that face is a citation-format defect, not a styling preference.
   ⚠️ THIS IS NOT A SECOND DIFFERENCE BETWEEN THE TWO GROUPS. It is a difference between a name and
   a sentence, and the relied group would take the same rule if it could ever render one — it
   cannot, because its rows resolved. The rows Adam is looking at (a named, unresolved authority)
   are `data-shape="name"` and get the relied card's type exactly. */
/* 🔴 RUN AC · BLOCK 6 — `cite` IS AN AUTHORITY NAME, NOT A SENTENCE. A statute's citation is
   its name (see packages/ui/seeAlsoLabel.mjs), so it keeps the name's size and ink and drops only
   the italic, which Indigo R2.1 reserves for CASE names. Three lines, so that the shape the shared
   rule now returns has a face here too; Clark takes no other change from that run. */
.cc-clark-sources-sa[data-shape="cite"] .cc-auth__nm { font-style: normal; }
/* 🔴 RUN RQ · §1 — `[data-shape="attributed"]` IS DELETED FROM THIS SELECTOR, not left in it.
   That shape no longer exists: `seeAlsoLabel` returns `cite` for every row with a citation and no
   name, so the row Adam was looking at now takes the rule ABOVE — the NAME's 13px `--cc-ink`, set
   roman. MEASURED, `--cc-ink` on the `--cc-bg-subtle` ground below: 16.99:1 light, 15.96:1 dark,
   14.17:1 gavel, against 6.93 / 8.98 / 6.97 for the muted sentence face it is leaving.
   ⚠️ `words` STAYS AND STILL EARNS THIS RULE. It is reachable — a row with neither a name nor a
   cite renders *"An authority this answer named could not be identified."*, which is a sentence
   the product speaks, and Indigo R2.1 reserves the serif italic above for CASE NAMES. */
.cc-clark-sources-sa[data-shape="words"] .cc-auth__nm {
  font-family: var(--cc-font-body);
  font-style: normal;
  font-size: 12.5px;
  line-height: 1.4;
  color: var(--cc-ink-secondary);
}

/* 🔴 RUN RQ · §1 — `.cc-clark-sources-sa-cited` IS DELETED WITH THE SHAPE THAT USED IT. It set the
   model's citation in italic inside `<q>` marks, because it was being QUOTED inside a sentence
   rather than cited. There is no sentence now: the citation IS the card's name and it is set in the
   name's face by the rule above. A rule left styling a class nothing emits is how the next reader
   concludes the sentence is still reachable. */

/* 🔴 THE GROUP NOTE MOVES ABOVE THE CARDS — MEASURED, NOT PREFERRED. Under a bare list it read as
   a footnote to the list. Under CARDS it reads as a caption on the LAST CARD, and it is the claim
   that licenses the entire group. MEASURED on the before arm at 1440x1100
   (ops/run-vl/out/rail-before-wall-1440-precedent.json): its top was y=1087 in a rail box ending
   at y=1078 — the sentence qualifying three unverified authorities sat below the fold while all
   three of them were on screen. Above the cards it is read before them, which is the order the
   claim has to arrive in, and the margins invert with the move. */
.cc-clark-sources-sa-note {
  margin: 0 0 8px;
  font-family: var(--cc-font-body);
  font-size: 0.6875rem;
  font-style: italic;
  line-height: 1.4;
  color: var(--cc-ink-muted);
}

/* ══ 🔴🔴 RUN SH · SH-1 — THE `COULD NOT BE CHECKED` GROUP. TWO SENTENCES, TWO POSITIONS. ═══════
   `railPayload.js` used to `.concat` `stripped` and `unchecked` into the group above, under *"Not
   found in our corpus, so we cannot vouch for it."* That sentence is true of `stripped` and false
   of `unchecked`, and it is the *TransUnion* defect stated in CSS terms: a group can only carry one
   note, so a group that holds both kinds of row must lie about one of them.

   🔴 THE CARDS ARE NOT RESTYLED. Both groups' items carry `.cc-clark-sources-sa`, so every rule
   above — the black name, the suppressed hover, the sentence-not-a-case-name face, the 7px rhythm —
   applies to both through the SAME selectors. ⭐ The distinction a reader needs is between two
   CLAIMS, and a claim is made of words. Restyling the cards would say the authorities differ; they
   do not. What differs is what we know about them.

   🔴 `--uc-note` SITS ABOVE THE CARDS AND CARRIES THE WHOLE SENTENCE. It shipped first as two
   paragraphs, the second one BELOW the cards. Measured on the painted rail: that second paragraph
   was below the fold in 8 of 9 fixture x viewport combinations (y=1038-1084 in a rail box ending at
   y=1078 at 1440x1100; 300-400px past the edge at 1280x800) — RUN VL's own finding, reproduced
   against a new sentence at the viewport VL measured. The rail scrolls, so it was reachable, and
   reachable is not read. ops/run-sh/out/fold.json. */
.cc-clark-sources-uc-note {
  margin: 0 0 8px;
  font-family: var(--cc-font-body);
  font-size: 0.6875rem;
  font-style: italic;
  line-height: 1.4;
  color: var(--cc-ink-muted);
}
/* 🔴 AND THE TWO GROUPS MUST NOT READ AS ONE RUN OF CARDS. Every other group on this rail is
   separated by the subhead's own `12px 0 7px`, which is the rhythm INSIDE a block of related
   groups. These two are not related — that is the entire point — so the boundary between them is
   set wider than any boundary within them. It is the only geometric difference between the two
   groups, and it exists so a reader's eye stops here rather than carrying the note above down over
   rows it is false of. */
.cc-clark-sources-subhead--split {
  margin-top: 22px;
}
/* The see-also group's heading sits at the same size as the others — it is a group heading and
   demoting it would hide the one group a reader most needs to notice — but it is not the same
   claim, so it does not share their weight. */
.cc-clark-sources-subhead--quiet {
  font-weight: 500;
}

/* 🔴🔴 RUN VJ — THE BREAKPOINT. `1199px` WAS `900px` ALL ALONG, AND THE 299px IN BETWEEN WAS
   ADAM'S NAVY VOID.

   THE DEFECT, as RUN VD measured it (`ops/run-vd/`, reproduced by `ops/run-vj/probe/band.mjs`).
   One layout change was written at TWO breakpoints, in two bundles, 300 lines apart in this file:
   `.cc-clark-main` turned to a column at 900 and `.cc-clark-sources` put its stacked clothes on at
   1199. Between 901 and 1199 the rail therefore wore STACKED declarations inside a box that was
   still a ROW: `flex: 1 1 auto` handed it 649px of an 1100px window while `max-height: 40vh`
   capped it at 280px of a 614px column — too wide and too short at once. The thread was crushed to
   **187-257px** with the answer clipped mid-word (it is 640-880px on either side of the band), and
   the shell's own `--cc-page` ground painted the remainder: **24.9%-31.7% of an 8px sample grid**,
   which on Adam's theme is navy.

   ⭐ 900 IS NOT A NEW NUMBER, IT IS A RESTORED RULING. BUNDLE V7 · LANE 7 — the comment 300 lines
   above — MEASURED 1100 as still working ("rail 258px beside an 842px thread, both usable") and
   chose 900 deliberately, as "the width below which the rail stops being a column and starts being
   a gutter". This block never argued with that ruling; it just carried a different number.

   ⚠️ AND `1199` WAS NOT ARBITRARY — IT WAS THE RIGHT NUMBER FOR THE WRONG SHELL. 1199 is the
   CITATIONS shell's drawer breakpoint (`chrome/Shell.css:62`, `chrome/TopBar.css:19`), which is
   why the sentence this comment replaced said "below the drawer breakpoint". Clark is not that
   shell. Clark's drawer breakpoint is **959.98px** (`chrome/ClarkShell.jsx:31`), and ClarkShell.css
   already carries a rule whose whole job is to hide Citations' ☰ between 960 and 1199 because it
   would toggle a drawer that is not in drawer mode. A number copied from a neighbouring shell is
   the hardest kind of wrong to see: it is a real breakpoint, just not this one's.
   ⭐ THE COMMENT WAS THE TELL, AND IT WAS RIGHT THERE FOR TWO WEEKS. A rule that contradicts its
   own stated rationale is not a style choice; it is a bug with a receipt attached.

   🔴 900 AND 959.98 ARE NOW DIFFERENT NUMBERS DOING DIFFERENT JOBS, ON PURPOSE. 900 is where the
   rail stops being a column; 959.98 is where the history sidebar becomes a drawer. Nothing in this
   run touches the second, and the drawer is DRIVEN open and closed on both sides of it
   (`ops/run-vj/out/drawer-after.json`) rather than reasoned about. */
@media (max-width: 900px) {
  .cc-clark-sources {
    flex: 1 1 auto;
    border-left: 0;
    border-top: 1px solid var(--cc-hairline);
    max-height: 40vh;
  }
}

/* ═══ BUNDLE E · LANE 2 — THE ANSWER STATE ════════════════════════════════════════════════════
   Spec: apps/main/ops/bundle-e/clark_v3_mockup.html (Answer state). */

/* ── The answer, in serif, once ──
   The old layout was EMITTED, not designed: one sentence of answer, then eight rows of table,
   then the same conclusion again in a callout. What is left is the answer — and it is set in the
   display serif at reading size because it is the one thing on the surface a user came for. */
.cc-ans-prose {
  font-family: var(--cc-font-display);
  /* 🔴 STREAM C — 19px/1.55 IS THE MOCKUP'S `.answer`, AND IT IS NOW THE CONTRACT FOR BOTH
     SURFACES. Clark rendered 19/1.52 and Research rendered the same answer at 15.5/1.6; the brief
     asks for one contract and says the founder prefers the larger. The mockup supplies the exact
     pair, so Research moves to it (apps/steel/client/src/styles/answer.css) rather than the two
     meeting somewhere in between. 1.52 was close enough to look right and is not the number. */
  font-size: 19px;
  line-height: 1.55;
  color: var(--cc-ink);
  max-width: var(--cc-clark-prose, 720px);
}
/* These two exist only to give the lead and the close their spacing inside a composed answer,
   where the server has put other sections between them. */
.cc-ans-prose--lead { margin-top: 0; }
.cc-ans-prose--closing { margin-bottom: 0; }

/* ═══ 🔴 RUN RA — THE PARAGRAPH, WHICH THIS STYLESHEET HAD NO RULE FOR ════════════════════════
   `.cc-ans-prose` has been 19px/1.55 since STREAM C and that half was already right. What it never
   had is a rule for a `<p>`, because the renderer never emitted one — it emitted a single inline
   flow, so a paragraph break in the model's prose collapsed to a space (measured: R1's five breaks
   rendered as 0, `ops/run-ra/_probe-collapse.mjs`).

   `.7em` is `MEASUREMENTS.md`'s `.answer p { margin: 0 0 .7em }`, transcribed rather than chosen —
   13.3px at 19px, a little under half a line of the 29.45px leading, which is what separates two
   paragraphs without letting either float free of the other.

   ⚠️ `:last-child` ZEROES THE TRAILING MARGIN. Without it the last paragraph pushes `.7em` of dead
   space into the gap above the stripped/see-also notices, and those already own their spacing —
   the answer would have grown a margin nobody declared, on every turn, only visible against the
   block beneath it. */
.cc-ans-prose p { margin: 0 0 .7em; }
.cc-ans-prose p:last-child { margin-bottom: 0; }

/* ═══ 🔴🔴 RUN RA — THE DECLINE ═══════════════════════════════════════════════════════════════
   SELECTORS DECLARED BY THIS RUN, and nothing else in this file is touched:
     .cc-ans-prose p            .cc-ans-prose p:last-child
     .cc-ans-decline            .cc-ans-decline-head        .cc-ans-decline-body
     .cc-ans-decline-searched   .cc-ans-decline-read        .cc-ans-decline-read-head
     .cc-ans-decline-read-list  .cc-ans-decline-read-link   .cc-ans-decline-read-cite

   🔴 TOKENS ONLY — no raw colour. `ops/enforce/run.mjs` fails the build on one, and a design that
   assumes navy dies on En Banc, where the darkest pixel on the surface IS the ground.

   ⭐ IT IS SET IN THE ANSWER'S OWN TYPE, NOT IN ERROR TYPE. A decline is what the product does when
   it is working; giving it a warning colour, an icon or a tinted box would file it under "something
   went wrong", which is the one reading that is false. The head is the answer's serif at the
   answer's size — it IS the answer — and only the supporting lines step down. */
.cc-ans-decline {
  max-width: var(--cc-clark-prose, 720px);
}
.cc-ans-decline-head {
  font-family: var(--cc-font-display);
  font-size: 19px;
  line-height: 1.55;
  color: var(--cc-ink);
  margin: 0 0 .7em;
}
.cc-ans-decline-body {
  font-family: var(--cc-font-display);
  font-size: 19px;
  line-height: 1.55;
  color: var(--cc-ink);
  margin: 0 0 .7em;
}
/* The ledger line: what was searched and how much came back. Muted and small — it is provenance,
   not the message, and it must not compete with the sentence above it for first read. */
.cc-ans-decline-searched {
  font-size: 12.5px;
  line-height: 1.45;
  color: var(--cc-ink-muted);
  margin: 0 0 18px;
}
.cc-ans-decline-read { margin: 0; }
/* 10.5px / .1em / uppercase / 600 is MEASUREMENTS.md's rail-heading step, reused so this heading
   reads as the same KIND of label the authorities rail uses rather than inventing a third. */
.cc-ans-decline-read-head {
  font-size: 10.5px;
  letter-spacing: .1em;
  text-transform: uppercase;
  font-weight: 600;
  color: var(--cc-ink-muted);
  margin: 0 0 10px;
}
.cc-ans-decline-read-list { list-style: none; margin: 0; padding: 0; }
.cc-ans-decline-read-list li {
  margin: 0 0 8px;
  line-height: 1.4;
}
.cc-ans-decline-read-list li:last-child { margin-bottom: 0; }
/* 🔴 --cc-accent-TEXT, NOT --cc-accent-FILL. v7's RULE 1 is explicit that collapsing the two put a
   fill colour on reading text once already; only `-text` is verified ≥4.5:1 against the card. */
.cc-ans-decline-read-link {
  font-family: var(--cc-font-display);
  font-size: 15px;
  color: var(--cc-accent-text);
  text-decoration: none;
}
a.cc-ans-decline-read-link:hover { text-decoration: underline; }
/* Bluebook R2.1 — a case name is italic. This one is safe to set italic because the row is
   corpus-resolved (groundingGate.js filters `readInstead` on `corpusTraceable`); the model-asserted
   rows elsewhere on this surface are deliberately NOT styled as citations. */
.cc-ans-decline-read-cite {
  display: block;
  font-family: var(--cc-font-display);
  font-style: italic;
  font-size: 13px;
  color: var(--cc-ink-muted);
  margin-top: 2px;
}

/* 🔴 BUNDLE V7 · LANE 3d — THE CONCLUSION, SET APART BY SPACE AND WEIGHT. NOTHING ELSE.
   The v7 mockup has no slot for `bottomLine` (it is Clark-only — Research never passes one), so
   this treatment is a decision rather than a transcription, and it is deliberately the smallest
   one that works: SAME family, SAME size, SAME colour as the prose it concludes. What separates
   it is a line of space and a half-step of weight.

   🔴 WHAT IT MUST NOT BECOME. Bundle E deleted a labelled "Bottom line" callout because it
   restated the prose's conclusion inside a box — a two-sentence answer then said the same thing
   twice with a panel between the halves. So: no label, no box, no border, no tint, no accent
   colour. A reader should register "this is the conclusion" from position and weight, never from
   furniture. If a later bundle finds itself adding a heading here, that is the callout coming
   back and Bundle E's finding applies to it again.

   The `text-wrap: pretty` is not decoration: a conclusion is the one line most likely to be read
   alone, and a one-word last line reads as a typo in a document a lawyer is about to rely on. */
.cc-ans-bottomline {
  margin: 1.05em 0 0;
  font-family: var(--cc-font-display);
  font-size: 19px;
  line-height: 1.52;
  font-weight: 600;
  color: var(--cc-ink);
  max-width: var(--cc-clark-prose, 720px);
  text-wrap: pretty;
}

/* ── Actions: ONE primary, one secondary ──
   B4's row had five controls of equal weight, so nothing in it was primary.
   🔴 STREAM C — THE `margin-top: 22px` THAT STOOD HERE IS DELETED, NOT EDITED. It was a SECOND
   declaration of a property already set 370 lines above, and it silently beat the corrected value
   on load order — so setting the mockup's 20px up there measured as no change at all. Two rules
   for one property in one file is how a value becomes unfixable; the geometry lives in one place
   now and this block keeps only what is genuinely about primary-vs-secondary. */
.cc-ans-action--primary {
  border-color: transparent;
  background: var(--cc-accent-fill);
  color: var(--cc-on-accent);
  font-weight: 600;
}
.cc-ans-action--primary:hover {
  border-color: transparent;
  background: var(--cc-accent-fill-hover);
}

/* ── 🔴 STREAM C — THE RAIL-CARD BLOCK IS DELETED WITH THE MARKUP IT DESCRIBED ──
   `.cc-ans-card-hit` and every `[data-variant="column"]` rule below it addressed elements that no
   JSX emits since the card moved to @citeclerk/ui. Two of its findings are PRESERVED there rather
   than lost, because both were earned:
     · the whole card is the link, not a repeated "Open in Research →" down a 280px column — that
       is `.cc-auth__hit` wrapping the body, and the shared card has exactly one hit area so it
       cannot regress;
     · the hover lives on the CARD so the whole block lights up, not just the text under the
       pointer — `.cc-auth:hover`, and the mockup's version of it is a border change to the accent
       rather than a fill change.
   The `data-variant` attribute is still SET by AuthorityCard.jsx and is still useful as a test and
   debugging handle; what is gone is styling hung off it. */

/* ── Compact treatment pill (the rail) ──
   Same flag, same tone, same glyph — a shorter spelling of the SAME state (parts.jsx
   TREATMENT_COPY.short), never a second vocabulary. The full sentence stays as the accessible
   name, so a screen reader is never handed the abbreviation. */
.cc-tx-pill[data-compact="1"] {
  padding: 3px 9px;
  border-radius: var(--cc-chip-radius);
  font-size: 0.6875rem;
  font-weight: 600;
  white-space: nowrap;
}
/* The tinted grounds are the invariant semantic layer's, so green flips to gold on the
   green-accent themes and lifts on En Banc's dark card — automatically, because these are the
   real role tokens rather than the hardcoded fallbacks this file used to carry. */
/* 🔴 THE COMPACT PILL IS INK-ON-CARD WITH A NEUTRAL EDGE — NOT A SOLID FILL. TWO CUTS GOT HERE.
   Cut 1 used the -bg tint with the -text ink: 4.34:1 (clear) and 4.00:1 (caution) against a 4.5
   floor for 11px type. Those tinted pairs are built for a full-size chip, not a compact one.
   Cut 2 used the solid -fill with --cc-on-status, which invariants.mjs documents as the on-colour
   for "the citator's treatment tiers". That is right on the LIGHT family and wrong on En Banc:
   --cc-on-status is a fixed white, and on the one dark-card theme the status solids INVERT to
   light (GOODLAW_GREEN_DARK, and STATUS_DARK's amber and rose) because there they are designed
   as TEXT on a dark card. White on light amber measured 1.60:1.
   🔴 So the claim in invariants.mjs — "none of them flip per theme, so the text that sits ON them
   doesn't flip either" — does not hold for the good-law/status family on dark-card themes. That is
   a gap in the shared layer, reported in the morning report rather than patched from here.
   What DOES hold on every theme is the pairing the layer was built around: the -text ink read on
   a CARD. So the compact pill carries its tone in the ink and the glyph, with a neutral hairline
   giving it its chip shape. Theme-invariant by construction, and it never needs to know whether
   the card underneath it is light or dark. */
.cc-tx-pill[data-compact="1"] {
  border: 1px solid var(--cc-hairline);
  background: transparent;
}
.cc-tx-pill[data-compact="1"][data-tone="clear"]    { color: var(--cc-success-text); }
.cc-tx-pill[data-compact="1"][data-tone="negative"] { color: var(--cc-error-text); }
/* 🔴 --cc-warning, NOT --cc-warning-text, AND THIS ONE IS A PLATFORM FINDING.
   invariants.mjs's STATUS_LIGHT.warningText measures 4.47:1 on PURE WHITE —
   below the 4.5 AA floor for body text before any surface tint is applied, so no choice of ground
   available to this file could have cleared it. Every other tone's -text ink clears comfortably
   (clear 6.2:1, gold 5.15:1, negative 5.5:1); caution is the only one. --cc-warning is the same
   semantic role one step darker and measures 5.02:1.
   The contract does not catch this because warningText is not in CONTRAST_PAIRS — the gate
   verifies the pairs it declares, and this pair was never declared. Reported for the theme owner
   rather than patched in packages/ui, which is out of this bundle's fence and shared by every
   surface in the repo. */
.cc-tx-pill[data-compact="1"][data-tone="caution"]  { color: var(--cc-warning); }
.cc-tx-pill[data-compact="1"][data-tone="pending"]  { color: var(--cc-ink-muted); }

/* ── A2 · THE FAILURE ROW ────────────────────────────────────────────────────────────────────────
   🔴 WHAT IS DELIBERATELY ABSENT IS THE SPECIFICATION. There is no `border`, no `background`, no
   `border-radius`, no `box-shadow`, no `::before` glyph and no warning hue anywhere in this block —
   the brief called for no box, no icon, no exclamation mark and no yellow/amber, and the way to
   guarantee that is to never declare them. `clarkFailureState.test.js` asserts their absence over
   this stylesheet, so a later "let's make the error stand out" edit fails the suite rather than
   shipping.

   It also does NOT reuse `.ccv2-clark-bubble` or sit inside `AssistantRow`. The whole defect was a
   failure that looked like an answer; sharing the answer's chrome would reintroduce it. */
.cc-clark-failure {
  /* Aligned to the assistant content column (40px orb + 12px gap) so the line sits under the
     conversation rather than beside it — without borrowing the orb or the byline. */
  margin: 2px 0 10px 52px;
}

.cc-clark-failure__line {
  margin: 0;
  font-family: var(--cc-font-sans, inherit);
  font-size: 13.5px;
  line-height: 1.5;
  /* Muted foreground, and nothing else. Recedes rather than alarms. */
  color: var(--cc-ink-muted);
}

/* The retry/sign-in affordance is INLINE TEXT, not a button shape: a filled or outlined control
   would reintroduce the box the brief rules out, and at this altitude a second button competes
   with the composer for the user's next action. */
.cc-clark-failure__action {
  appearance: none;
  border: 0;
  background: none;
  padding: 0;
  font: inherit;
  color: var(--cc-ink-muted);
  text-decoration: underline;
  text-underline-offset: 2px;
  cursor: pointer;
}

.cc-clark-failure__action:hover,
.cc-clark-failure__action:focus-visible {
  color: var(--cc-ink);
}

/* ── RUN AP · PART 2 — the stage line, beside the dots ──────────────────────────────────────────
   🔴 WHAT IT IS FOR. Clark paints structure at 9 ms and then, on a cold turn, nothing on the page
   changes for up to forty seconds (RUN AN: cold p90 40,562 ms). The dots said "working"; they could
   not say WHICH of the four things the pipeline is doing, so a slow answer and a hung one looked
   identical — and before RUN AP's PART 1 roughly one turn in four really was hung.

   🔴 IT BORROWS THE ERROR GRAMMAR ON PURPOSE: muted, one line, no box, no icon, no tint, no accent
   colour. Bundle E deleted a labelled callout for restating the prose inside furniture, and the
   same finding applies here — a progress indicator that arrives as a panel is a second answer-sized
   object on screen competing with the answer it is waiting for. Position and muting carry it.

   ⚠️ NOT MONOSPACE, and that is the one deliberate departure from `.cc-ans-trace-row` above. Those
   are machine CHECKS and should read as machine output; this is a sentence addressed to the reader
   about what is happening now. Same muted ink, same restraint, human typeface.

   The fade is 0.22s and REUSES `cc-ans-trace-in`, so a stage changing does not snap. That block
   further up keys on SELECTORS rather than on the keyframe, so this class is named in it
   explicitly — checked rather than assumed, because an inherited opt-out that turns out not to
   apply is exactly the kind of silence this file keeps paying for. */
.cc-ans-stage {
  margin: 0.5em 0 0;
  font-family: var(--cc-font-body);
  font-size: 0.8125rem;
  line-height: 1.5;
  color: var(--cc-ink-muted);
  animation: cc-ans-trace-in 0.22s ease both;
}

@media (max-width: 768px) {
  /* 🔴 RUN FU · STEP 3 — TOUCH TARGETS, MEASURED AS HIT RECTANGLES.
     The glyphs do not move. A transparent `::after` centred on each control grows the rectangle a
     thumb actually gets to 44px (Apple HIG / WCAG 2.5.5 AAA, and this product's own RUN CD
     precedent) while the painted chip keeps its compact size — the density here is a decision, and
     a blanket 44px box would destroy it. Vertical growth is free in a horizontal rail: there is no
     neighbour above or below to take the overlap. Nothing above 768px is touched. */
  .cc-ans-cite { position: relative; margin-block: 10px; }
  .cc-ans-cite::after { content: ""; position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%); width: 100%; min-width: 44px; height: 44px; }
  .cc-ans-trace-pill { position: relative; margin-block: 10px; }
  .cc-ans-trace-pill::after { content: ""; position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%); width: 100%; min-width: 44px; height: 44px; }
}

/* ══ 🔴 RUN RM — THE AUTHORITY RAIL ON A PHONE ═══════════════════════════════════════════════
   Spec: design/mobile-authority-rail-mockup.html. Component: `MobileAuthorities`
   (answer/AuthorityCard.jsx), whose header carries the before-measurement this replaces.

   🔴 599.98, AND WHY IT IS A NEW NUMBER RATHER THAN ONE OF THE TWO THAT EXIST. Clark already has
   900 (where `.cc-clark-main` becomes a column and the rail stacks) and 959.98 (where the history
   sidebar becomes a drawer, chrome/ClarkShell.jsx). Neither is this boundary. MEASURED on the
   served page: at 768 the stacked rail hides 1px of its content and is usable; at 414 and below it
   hides 41–62px and cuts the card. 768 is a tablet where the stacked rail WORKS, so moving it
   would be a regression dressed as consistency. This number is the phone, and it is deliberately
   below 768 so that everything §6 asks to be proved unchanged is untouched by construction.

   The three rules below are one decision: on a phone the authorities are IN the thread, not in a
   bounded box under the composer. */

/* 1 — the control is desktop-invisible. It renders on every answer; only the phone shows it. */
.cc-authmob { display: none; }

@media (max-width: 599.98px) {
  /* 2 — the stacked rail is replaced, not merely hidden: everything it showed is now in the
     thread, above, where it cannot be clipped. Hiding it without the control would delete the
     proof; the control is rendered by the same predicate that decides the rail has anything. */
  .cc-clark-sources { display: none; }

  /* 3 — one control, one job. `Show Authorities` opens the in-thread panel that the rail
     duplicated; on a phone the new control IS that disclosure, so the button would be a second
     control doing one thing. ⚠️ This takes the action row from three buttons to two at phone
     widths — still within CK's `verify-visibility` assertion (exactly two or three), which is
     therefore NOT loosened. Reported rather than adjusted. */
  .cc-v2-actions [data-testid="clark-action-authorities"] { display: none; }

  /* 🔴 RUN CK · A1 — the grid that must come with the hidden third button is set in
     answerV2.css, NOT here. answer.css is imported BEFORE answerV2.css (ClarkAnswerV2.jsx:60-61),
     so a media rule in this file loses to that file's unmediated `1fr 1fr 1fr` base rule at equal
     specificity. Measured before moving it: the row still computed three columns at 599px. */

  .cc-authmob {
    display: block;
    margin: 14px 0 2px;
    border: 1px solid var(--cc-edge);
    border-radius: 11px;
    background: var(--cc-card);
    overflow: hidden;
  }

  /* ⚠️ 46px, the mockup's number and above the 44px floor. The whole row is the target, not the
     chevron: a tap anywhere on the summary line expands. */
  .cc-authmob-btn {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    width: 100%;
    min-height: 46px;
    padding: 12px 14px;
    background: none;
    border: 0;
    font-family: inherit;
    font-size: 13.5px;
    font-weight: 650;
    color: var(--cc-accent-text);
    cursor: pointer;
    text-align: left;
  }

  .cc-authmob-btn:focus-visible {
    outline: 2px solid var(--cc-accent-text);
    outline-offset: -2px;
  }

  .cc-authmob-chev { font-size: 12px; opacity: 0.55; }

  /* The cards sit INSIDE the border, in the thread flow — the fix. No max-height, no overflow:
     they scroll with the conversation because they ARE the conversation's content now. */
  .cc-authmob-cards {
    border-top: 1px solid var(--cc-edge);
    padding: 12px;
  }

  /* The rail's own list, unchanged — `.cc-ans-panel-list.cc-clark-sources-list` renders the same
     `RailItem`. Only the gap is restated, because the rail's spacing came from a column that is
     not here. */
  .cc-authmob-cards .cc-clark-sources-list {
    display: grid;
    gap: 10px;
    margin: 0;
    padding: 0;
  }
}
